import React, { useState } from 'react';
import { makeStyles, mergeClasses, tokens, Text } from '@fluentui/react-components';

// Bendras, minimalistinis lentelės langelio turinys — mažas paveikslėlis +
// pilka etiketė + pusjuodė reikšmė, su hover overlay "Pasirinkti"/"Žiūrėti"
// veiksmais vietoj visada matomų atskirų mygtukų. Ištraukta iš
// PlatesFillingsAndHandles.jsx į bendrą komponentą, kad TAS PATS vizualinis
// stilius būtų naudojamas visose panašiose lentelėse (žr. /admin/bugs
// "Suvienodinti isvaizdavima, karkaso plokstes rodyti kaip yra padaryta
// plokstes filinai ir rankeneles, nuimti pilkus background").
//
// Apibrėžta modulio lygmenyje, NE tėvinio komponento render'o viduje —
// antraip React kiekvieną tėvinio komponento perpiešimą laikytų nauju
// komponento tipu ir iš naujo montuotų CellItem, prarasdamas vidinę
// `hovered` būseną (pelė "nustotų" veikti pusiaukelėje hover'inant).
const useStyles = makeStyles({
  cellItem: {
    position: 'relative',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px',
    cursor: 'pointer', padding: '4px', borderRadius: tokens.borderRadiusMedium,
    // Fiksuotas plotis + teksto apkarpymas (žr. cellItemTitle/cellItemSubtitle) —
    // be šito langelio plotis priklausydavo nuo ilgiausios teksto eilutės (pvz.
    // "Nuo paspaudimo" vs "Nėra"), tad "tas pats" langelis skirtinguose
    // kontekstuose realiai būdavo skirtingo pločio (žr. /admin/bugs "Visi tokie
    // elementai... turi buti tokio pacio stiliaus aukscio ir plocio absoliuciai
    // visame projekte").
    width: '90px', boxSizing: 'border-box',
    // Plonas rėmelis + žalias/brand rėmelis hover'inant — žr. /admin/bugs
    // "Uzdeti visiems siems elementams (bendrai visur) border" ir "visiems
    // tokio tipo elementams reikia pakeisti border color on hover" (tas pats
    // elgesys, kaip SelectionBox.jsx .box/.box:hover). SVARBU: `borderColor`/
    // `borderWidth`/`borderStyle` (be krypties) yra Griffel'io
    // UNSUPPORTED_CSS_PROPERTIES sąraše (@griffel/core/src/constants.js) —
    // Griffel jas TYLIAI IŠMETA (tik console.error, jokios CSS taisyklės),
    // tad anksčiau čia naudotos "ilgosios" savybės apskritai NEVEIKĖ (border
    // visiškai dingdavo, ne tik hover). Vietoj to: bazinis "border" shorthand
    // (palaikomas, išplečiamas į visas 12 krypčių savybių) + hover'yje VISOS
    // 4 kryptinės "borderXColor" (tikros, palaikomos ilgosios savybės) —
    // patikrinta gyvai, kad šis derinys patikimai nustelbia bazinį border'į.
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    transitionProperty: 'background-color, box-shadow, border-color', transitionDuration: tokens.durationFast,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground2,
      borderTopColor: tokens.colorBrandStroke1, borderRightColor: tokens.colorBrandStroke1,
      borderBottomColor: tokens.colorBrandStroke1, borderLeftColor: tokens.colorBrandStroke1,
    },
  },
  // Pasirinkto elemento žymėjimas — tas pats vizualinis kalba (žalias/brand
  // kontūras), kaip SelectionBox.jsx boxActive, tik pritaikyta mažesniam
  // langeliui (žr. BasisSelectingDialog/PanelSelectingDialog grid'us, kur
  // reikia matyti, kuris elementas šiuo metu pasirinktas).
  cellItemSelected: {
    boxShadow: `inset 0 0 0 2px ${tokens.colorBrandStroke1}`,
    backgroundColor: tokens.colorBrandBackground2,
  },
  // variant="list" opt-in (žr. CellItem props žemiau) — be rėmelio, kairinis
  // lygiavimas, platesnė teksto zona, glaudesnis tarpas — skirta lentelės
  // eilutėms (PlatesFillingsAndHandles.jsx), KUR langelis jau turi savo
  // vizualinį kontekstą (lentelės eilutė su hover paryškinimu), tad antras,
  // atskiras rėmelis aplink kiekvieną langelį atrodo pertekliškai. Numatytasis
  // "grid" variantas (bet koks kitas naudojimas, pvz. BasisSelectingDialog/
  // PanelSelectingDialog grid'ai) lieka NEPAKEISTAS.
  // width:'100%' (ne fiksuotas px) — pildo TIKRĄ turimą stulpelio plotį (žr.
  // PlatesFillingsAndHandles.jsx s.dataCell — calc() lygiai dalina siaurą,
  // ~300px panelį per 3 stulpelius), ne atvirkščiai (fiksuotas CellItem plotis
  // priverstų stulpelį/lentelę peraugti konteinerį ir "Rankenėlės" stulpelis
  // nukeliautų už matomos srities, kaip atsitiko su ankstesniu 124px bandymu).
  cellItemList: { border: 'none', alignItems: 'flex-start', width: '100%', gap: '1px' },
  // lineHeight sutankintas (paveldimas vaikams title/subtitle, kurie patys
  // savo lineHeight neapibrėžia) — numatytasis naršyklės/Fluent line-height
  // (~1.3-1.5x) tarp dviejų 10-11px eilučių atrodė per platus tarpas.
  cellItemTextLeft: { textAlign: 'left', lineHeight: '1.15' },
  cellItemImage: {
    width: '44px', height: '44px', borderRadius: tokens.borderRadiusMedium, overflow: 'hidden',
    backgroundColor: tokens.colorNeutralBackground3, display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0, color: tokens.colorNeutralForeground4,
  },
  cellItemImg: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  cellItemText: { textAlign: 'center', width: '100%' },
  cellItemTitle: {
    display: 'block', fontSize: '10px', color: tokens.colorNeutralForeground3,
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  cellItemSubtitle: {
    display: 'block', fontSize: '11px', fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground1,
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  // Papildoma (neprivaloma) eilutė po subtitle — pvz. kaina (žr.
  // PanelSelectingDialog.jsx), tas pats apkarpymas dėl vienodo pločio.
  cellItemFooter: {
    display: 'block', fontSize: '10px', fontWeight: tokens.fontWeightBold, color: tokens.colorNeutralForeground2,
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%',
  },

  cellItemOverlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.72)', borderRadius: tokens.borderRadiusMedium,
    display: 'flex', position: 'absolute', top: '2px', left: '2px', right: '2px', bottom: '2px',
    flexDirection: 'column',
    transitionProperty: 'opacity, visibility', transitionDuration: '0.1s', transitionTimingFunction: 'ease-in-out',
    opacity: 0, visibility: 'hidden', zIndex: 1,
    '& > div': {
      display: 'flex', width: '100%', height: '50%', color: '#fff',
      fontSize: '10px', justifyContent: 'center', alignItems: 'center', cursor: 'pointer', textAlign: 'center',
    },
    '& > div:hover:not(.disabled-link)': { textDecorationLine: 'underline' },
    '& > div:first-child': { borderBottom: '1px solid rgba(255, 255, 255, 0.85)' },
    '& > div.disabled-link': { color: 'rgba(255, 255, 255, 0.4)', cursor: 'not-allowed' },
  },
  // variant="list" — langelis platesnis (124px, žr. cellItemList), bet patys
  // "Pasirinkti"/"Žiūrėti" mygtukai neturi tęstis per visą tą plotį (atrodė kaip
  // pernelyg ištemptas juodas ruožas) — siauresnis overlay vietoj kraštas-iki-
  // krašto, kairinamas su paveikslėliu (kuris pats taip pat kairinamas šiame
  // variante, žr. cellItemList alignItems:'flex-start'), ne centruojamas.
  // Plotis liečia tik šį overlay'ų, ne patį hover suveikimo plotą (tas lieka
  // visas cellItem, žr. onMouseEnter aukščiau).
  cellItemOverlayNarrow: {
    left: '2px', right: 'auto', width: '74px',
  },
  cellItemOverlayVisible: { opacity: 1, visibility: 'visible' },
});

const CellItem = ({ image, isIcon, icon: Icon, title, subTitle, footer, selected = false, variant = 'grid', onClick, onView, viewDisabled }) => {
  const s = useStyles();
  const [hovered, setHovered] = useState(false);
  const isList = variant === 'list';
  const handleLinkClick = (e, fn, disabled) => {
    e.stopPropagation();
    if (fn && !disabled) fn();
  };
  return (
    <div
      className={mergeClasses(s.cellItem, isList && s.cellItemList, selected && s.cellItemSelected)}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className={s.cellItemImage}>
        {!isIcon && image && <img src={image} alt="" className={s.cellItemImg} onError={e => { e.target.style.display = 'none'; }} />}
        {isIcon && Icon && <Icon fontSize={20} />}
      </div>
      <div className={mergeClasses(s.cellItemText, isList && s.cellItemTextLeft)}>
        {title && <Text className={s.cellItemTitle} title={title}>{title}</Text>}
        {subTitle && <Text className={s.cellItemSubtitle} title={subTitle}>{subTitle}</Text>}
        {footer && <Text className={s.cellItemFooter} title={typeof footer === 'string' ? footer : undefined}>{footer}</Text>}
      </div>
      {(onClick || onView) && (
        <div className={mergeClasses(s.cellItemOverlay, isList && s.cellItemOverlayNarrow, hovered && s.cellItemOverlayVisible)}>
          {onClick && (
            <div onClick={(e) => handleLinkClick(e, onClick, false)}>Pasirinkti</div>
          )}
          {onView && (
            <div
              onClick={(e) => handleLinkClick(e, onView, viewDisabled)}
              className={viewDisabled ? 'disabled-link' : ''}
            >Žiūrėti</div>
          )}
        </div>
      )}
    </div>
  );
};

export default CellItem;
